import { FileText, GitCompare, TestTube2 } from 'lucide-react'
import { ExternalLink } from '@/components/common/ExternalLink'
import type { RunArtifacts as RunArtifactsType } from '@/lib/types'
import { cn } from '@/lib/utils'

export interface RunArtifactsProps {
  /** Artifact links */
  artifacts?: RunArtifactsType
  /** Additional CSS classes */
  className?: string
}

/**
 * Artifact links section for completed runs.
 * 
 * @example
 * ```tsx
 * <RunArtifacts artifacts={run.artifacts} />
 * ```
 */
export function RunArtifacts({ artifacts, className }: RunArtifactsProps) {
  if (!artifacts) {
    return (
      <div className={cn('text-sm text-muted-foreground', className)}>
        No artifacts available
      </div>
    )
  }

  const links = [
    { key: 'logs', label: 'View Logs', icon: FileText, url: artifacts.logs },
    { key: 'diff', label: 'View Diff', icon: GitCompare, url: artifacts.diff },
    { key: 'tests', label: 'View Test Results', icon: TestTube2, url: artifacts.tests },
  ].filter((link) => link.url)

  if (links.length === 0) {
    return (
      <div className={cn('text-sm text-muted-foreground', className)}>
        No artifacts available
      </div>
    )
  }

  return (
    <div className={cn('space-y-2', className)}>
      {links.map((link) => (
        <div key={link.key} className="flex items-center gap-2">
          <link.icon className="h-4 w-4 text-muted-foreground" />
          <ExternalLink href={link.url!}>{link.label}</ExternalLink>
        </div>
      ))}
    </div>
  )
}
